import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import config from "config"

import { ApiTypes } from "types"

import { getMobileAuthToken } from "utils/mobileAuthToken"

export const MOBILE_BASE_URL_API = config.MOBILE_BASE_URL_API

export interface OAuthCodeRequest {
  clientId: string
  redirectUri: string
  responseType: string
  state: string
  codeChallenge?: string
  codeChallengeMethod?: string
  scope?: string
}

export interface OAuthCodeResponse {
  redirectUrl?: string
}

export interface ForgotPasswordRequest {
  email: string
}

const buildOAuthCodeRequestBody = (body: OAuthCodeRequest) => {
  const { clientId, redirectUri, responseType, state, codeChallenge, codeChallengeMethod, scope } = body
  const params = new URLSearchParams()

  params.set("clientId", clientId)
  params.set("redirectUri", redirectUri)
  params.set("responseType", responseType)
  params.set("state", state)

  if (codeChallenge) {
    params.set("codeChallenge", codeChallenge)
  }

  if (codeChallengeMethod) {
    params.set("codeChallengeMethod", codeChallengeMethod)
  }

  if (scope) {
    params.set("scope", scope)
  }

  return params
}

export const mobileApi = createApi({
  reducerPath: "mobile",
  baseQuery: fetchBaseQuery({
    baseUrl: MOBILE_BASE_URL_API,
    prepareHeaders: (headers, api) => {
      const token = getMobileAuthToken()

      if (token && api.endpoint !== "forgotPassword") {
        headers.set("Authorization", `Bearer ${token}`)
      }

      return headers
    },
  }),
  endpoints: builder => ({
    mobileLogin: builder.mutation<ApiTypes.Res.MobileLogin, ApiTypes.Req.MobileLogin>({
      query: body => ({
        url: `/api/login`,
        method: "POST",
        body,
      }),
    }),
    forgotPassword: builder.mutation<null, ForgotPasswordRequest>({
      query: body => ({
        url: `/api/forgot-password`,
        method: "POST",
        headers: { Accept: "application/json" },
        body,
      }),
    }),
    getSmartHomeDevices: builder.query<ApiTypes.Res.SmartHomeDevices, void>({
      query: () => ({
        url: `/api/smart-home/devices`,
        method: "GET",
      }),
    }),
    requestOAuthCode: builder.mutation<OAuthCodeResponse, OAuthCodeRequest>({
      query: body => ({
        url: `/api/oauth/code`,
        method: "POST",
        body: buildOAuthCodeRequestBody(body),
      }),
    }),
    setSmartHomeAlexaEnabled: builder.mutation<void, ApiTypes.Req.SmartHomeDeviecsSetEnabled>({
      query: body => ({
        url: `/api/smart-home/alexa/set-enabled`,
        method: "POST",
        body,
      }),
    }),
    logoutMobileUser: builder.mutation<ApiTypes.Res.MobileAction, void>({
      query: () => ({
        url: `/api/logout`,
        method: "POST",
      }),
    }),
  }),
})

export const {
  useMobileLoginMutation,
  useForgotPasswordMutation,
  useGetSmartHomeDevicesQuery,
  useRequestOAuthCodeMutation,
  useSetSmartHomeAlexaEnabledMutation,
  useLogoutMobileUserMutation,
} = mobileApi
